import { unifiedCameraUtils } from './uniCamUtils'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useFeatureFlagsStore } from '@renderer/stores/featureFlagsStore'
import { getCachedOriginalSettings } from './cameraSettingsManager'
import {
  buildApplyPlan,
  captureEntriesFromConfig,
  detectMatchTier,
  indexByName,
  readCameraModel,
  type ApplyPlan,
  type ConfigNode,
  type MatchTier,
  type PresetEntry,
  type PresetGroup,
  type ResolvedWrite
} from './presetCompatibility'
import { detectCameraBrandFromConfig, type CameraBrand } from './captureRiskAssessment'

/**
 * Applying a camera preset — the part that touches real hardware.
 *
 * Everything here exists to satisfy one requirement: applying a preset must
 * never hang the booth, never leave the camera in a worse state than it found
 * it, and never lie about what happened.
 *
 * The threat model is concrete rather than theoretical:
 *
 * - **Liveview and config writes share ONE USB PTP channel**, and every write
 *   carries an 800ms settle on both backends (web-gphoto2 and the native
 *   gphoto2-daemon impose it deliberately, for parity). A 25-widget apply
 *   therefore blocks preview frames for its entire duration — which is exactly
 *   what "the booth hung" looks like from the floor. So the caller suspends the
 *   live preview first, and we park the camera on top of that.
 *
 * - **Nothing below us has a timeout.** `Camera.setConfigValue` awaits a WASM /
 *   sidecar op that can simply never settle (the WebUSB shim cannot cancel a
 *   transfer). Without a deadline here, one wedged op strands the apply — and
 *   the camera's op queue — forever. Every call is raced against a timeout.
 *
 * - **A timed-out op still owns the queue.** Racing a timeout does NOT cancel
 *   the underlying PTP transfer, so once one write times out we stop
 *   immediately and refuse to enqueue more work (including any rollback) rather
 *   than piling ops onto a jammed queue.
 *
 * - **Camera connect is the flakiest window in this app** (2,172 incidents /
 *   14 days, 28.6 min average downtime). Nothing here runs on connect: apply is
 *   always operator-initiated.
 *
 * Design: docs/camera-preset-plan.md §6.
 */

export const CAMERA_PRESETS_FLAG = 'camera_presets'
export const CAMERA_PRESET_CROSS_MODEL_FLAG = 'camera_preset_cross_model'

export type CameraKey = 'primary' | 'secondary'

export function isCameraPresetsEnabled(): boolean {
  return useFeatureFlagsStore.getState().getFlag(CAMERA_PRESETS_FLAG, false)
}

export function isCrossModelApplyEnabled(): boolean {
  return useFeatureFlagsStore.getState().getFlag(CAMERA_PRESET_CROSS_MODEL_FLAG, false)
}

/* ─────────────────────────── timing budget ──────────────────────────── */

/**
 * One write: 800ms settle + up to three PTP-busy retries at 250/500/750ms +
 * the transfer itself. 12s is comfortably above a healthy worst case and well
 * below the ~90s the WASM stack takes to surface a lost transfer.
 */
const WRITE_TIMEOUT_MS = 12_000
/** A full config-tree read over PTP; large on Canon EOS bodies. */
const CONFIG_READ_TIMEOUT_MS = 20_000
/** Whole-apply ceiling. Past this we stop even if writes are still succeeding. */
const APPLY_DEADLINE_MS = 150_000
/** Consecutive non-busy write failures before we declare the camera unwell. */
const MAX_CONSECUTIVE_FAILURES = 3
/** Let React unmount the preview and in-flight liveview reads drain. */
const PREVIEW_DRAIN_MS = 350

export class PresetTimeoutError extends Error {
  constructor(label: string, ms: number) {
    super(`${label} did not respond within ${ms}ms`)
    this.name = 'PresetTimeoutError'
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Race an operation against a deadline.
 *
 * NOTE: this does not cancel the underlying work — WebUSB has no transfer
 * cancellation and the sidecar op is already in flight. It bounds how long WE
 * wait. Callers must treat a timeout as "the camera's op queue may still be
 * busy" and stop issuing work, not as "that write failed, try another".
 */
async function withTimeout<T>(op: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      op,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => reject(new PresetTimeoutError(label, ms)), ms)
      })
    ])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

function isBusyError(error: unknown): boolean {
  const text = error instanceof Error ? error.message : String(error)
  return /device\s*busy|ptp.*busy|\b0x2019\b|2333736|2249016/i.test(text)
}

/* ─────────────────────────────── capture ────────────────────────────── */

export interface CapturedPreset {
  cameraModel: string
  cameraBrand: CameraBrand
  entries: PresetEntry[]
}

/**
 * Read the connected camera's current configuration into preset entries.
 *
 * The live-view brightness override is the hazard here: when the operator has
 * the brighten-for-flash toggle on, `applyLiveViewBrightness` has pushed
 * `shutterspeed` (and possibly `exposuresimulation`) away from the operator's
 * real choice and only restores it around capture. Snapshotting blind would
 * record 1/30 as the chosen exposure and then propagate it to every other
 * booth — so the saved originals win.
 */
export async function capturePresetFromCamera(
  cameraKey: CameraKey
): Promise<CapturedPreset | null> {
  const config = (await withTimeout(
    Promise.resolve(unifiedCameraUtils.getConfig(cameraKey)),
    CONFIG_READ_TIMEOUT_MS,
    'getConfig'
  ).catch(() => null)) as ConfigNode | null

  if (!config) return null

  const original = getCachedOriginalSettings(cameraKey)
  const overrides: Record<string, string | number | boolean> = {}
  if (original?.shutterSpeed) overrides[original.shutterSpeed.name] = original.shutterSpeed.value
  if (original?.exposureSimulation) {
    overrides[original.exposureSimulation.name] = original.exposureSimulation.value
  }

  return {
    cameraModel: readCameraModel(config),
    cameraBrand: detectCameraBrandFromConfig(config),
    entries: captureEntriesFromConfig(config, overrides)
  }
}

/* ──────────────────────────────── plan ──────────────────────────────── */

export interface PlanRequest {
  entries: PresetEntry[]
  presetModel: string
  presetBrand: CameraBrand
  enabledGroups: PresetGroup[]
}

export interface PlanResult {
  plan: ApplyPlan
  targetModel: string
  targetBrand: CameraBrand
  /** False when the tier needs cross-model apply and the flag is off. */
  allowed: boolean
}

/** Read the camera and work out exactly what an apply would do. Writes nothing. */
export async function planPresetApply(
  cameraKey: CameraKey,
  request: PlanRequest
): Promise<PlanResult | null> {
  const config = (await withTimeout(
    Promise.resolve(unifiedCameraUtils.getConfig(cameraKey)),
    CONFIG_READ_TIMEOUT_MS,
    'getConfig'
  ).catch(() => null)) as ConfigNode | null

  if (!config) return null

  const targetModel = readCameraModel(config)
  const targetBrand = detectCameraBrandFromConfig(config)
  const tier: MatchTier = detectMatchTier(
    request.presetModel,
    request.presetBrand,
    targetModel,
    targetBrand
  )

  const allowCrossModel = isCrossModelApplyEnabled()
  const plan = buildApplyPlan(request.entries, config, tier, {
    enabledGroups: request.enabledGroups,
    allowCrossModel
  })

  return {
    plan,
    targetModel,
    targetBrand,
    allowed: tier === 'exact' || allowCrossModel
  }
}

/* ─────────────────────────────── apply ──────────────────────────────── */

export type ApplyStatus =
  | 'completed' // every planned write succeeded
  | 'partial' // some writes failed, camera still healthy
  | 'aborted' // stopped early on repeated failures
  | 'timeout' // a write stopped responding; queue may still be busy
  | 'cancelled' // operator cancelled between writes
  | 'refused' // preconditions not met; nothing was written

export interface ApplyFailure {
  name: string
  message: string
}

export interface ApplyResult {
  status: ApplyStatus
  /** Writes that succeeded. */
  applied: number
  /** Of those, how many had their value adjusted to fit this camera. */
  adjusted: number
  /** Entries the plan never intended to write. */
  skipped: number
  failures: ApplyFailure[]
  durationMs: number
  /**
   * Did the camera still answer a config read after we finished? False means
   * the operator must be told to reconnect — never discovered by the next guest.
   */
  cameraHealthy: boolean
  /** Operator-facing explanation for refused / aborted / timeout. */
  reason?: string
}

export interface ApplyOptions {
  /**
   * Unmount the live preview (and await a frame). The engine cannot do this
   * itself — the preview is React-owned — and it is the load-bearing safety
   * step: `parkCamera` REFUSES while a Preview is mounted, so without this the
   * camera stays live and every write contends with the pull loop.
   */
  suspendPreview: () => void | Promise<void>
  /** Restore the preview. Always called, including on every failure path. */
  resumePreview: () => void | Promise<void>
  /** Checked between writes. An in-flight PTP op cannot be interrupted. */
  shouldCancel?: () => boolean
  /** Progress for the UI: 1-based index over the planned writes. */
  onProgress?: (done: number, total: number, label: string) => void
}

/** Values as they were before we touched them, for an operator-driven undo. */
export interface ApplyRollback {
  cameraKey: CameraKey
  capturedAt: number
  values: Array<{ name: string; value: string | number | boolean }>
}

const rollbackByCamera: Record<CameraKey, ApplyRollback | null> = {
  primary: null,
  secondary: null
}

export function getRollback(cameraKey: CameraKey): ApplyRollback | null {
  return rollbackByCamera[cameraKey]
}

export function clearRollback(cameraKey: CameraKey): void {
  rollbackByCamera[cameraKey] = null
}

/**
 * Single-flight. Two applies interleaving on one camera would race each other
 * through a serialized op queue and produce a state neither of them planned.
 */
const applyInFlight: Record<CameraKey, boolean> = { primary: false, secondary: false }

export function isApplyInFlight(cameraKey: CameraKey): boolean {
  return applyInFlight[cameraKey]
}

function refuse(reason: string, startedAt: number): ApplyResult {
  return {
    status: 'refused',
    applied: 0,
    adjusted: 0,
    skipped: 0,
    failures: [],
    durationMs: Date.now() - startedAt,
    cameraHealthy: true,
    reason
  }
}

/**
 * Apply a plan to the connected camera.
 *
 * Ordering is fixed and deliberate:
 *   suspend preview -> park -> snapshot -> write loop -> [finally] wake -> resume
 * The wake/resume pair is in a `finally` because leaving a booth parked with a
 * frozen preview is the single most likely way to "break the camera" from the
 * operator's point of view, and it must survive a thrown apply.
 */
export async function applyPresetPlan(
  cameraKey: CameraKey,
  plan: ApplyPlan,
  options: ApplyOptions
): Promise<ApplyResult> {
  const startedAt = Date.now()

  if (!isCameraPresetsEnabled()) return refuse('feature disabled', startedAt)
  if (applyInFlight[cameraKey]) return refuse('another apply is already running', startedAt)

  // Re-check the tier at write time, not just at plan time. The plan may have
  // been built minutes ago, the flag can have changed underneath it, and this is
  // the last gate before anything reaches the hardware.
  if (plan.tier !== 'exact' && !isCrossModelApplyEnabled()) {
    return refuse('cross-model apply is disabled', startedAt)
  }

  const store = useCameraStore.getState()
  const deviceType = cameraKey === 'primary' ? store.deviceType : store.secondaryDeviceType
  const camera = cameraKey === 'primary' ? store.camera : store.secondaryCamera
  if (deviceType !== 'camera' || !camera) {
    return refuse('no DSLR connected on this slot', startedAt)
  }
  if (plan.writes.length === 0) {
    return {
      status: 'completed',
      applied: 0,
      adjusted: 0,
      skipped: plan.skipped.length,
      failures: [],
      durationMs: Date.now() - startedAt,
      cameraHealthy: true,
      reason: 'nothing to change'
    }
  }

  applyInFlight[cameraKey] = true

  const failures: ApplyFailure[] = []
  let applied = 0
  let adjusted = 0
  let consecutiveFailures = 0
  let status: ApplyStatus = 'completed'
  let reason: string | undefined
  // Once a write times out the op queue may still be occupied; from that point
  // we issue NOTHING further — not the next write, not a health probe, not a
  // rollback. Piling ops onto a jammed queue is how a recoverable stall becomes
  // a dead camera.
  let queueSuspect = false

  const deadline = startedAt + APPLY_DEADLINE_MS

  try {
    // 1. Stop the pull loop. Without this, park() refuses (it will not park a
    //    camera whose liveview is on screen) and every write contends with a
    //    preview read on the same PTP channel.
    await options.suspendPreview()
    await sleep(PREVIEW_DRAIN_MS)

    // 2. Park on top of it. Best effort by design: returns false when the
    //    `camera_rest` flag is off or the body has no viewfinder widget, and
    //    that is a supported (slower, more contended) path, not an error.
    await unifiedCameraUtils.parkCamera(cameraKey).catch(() => false)

    // 3. Snapshot exactly the widgets we are about to change, so an operator can
    //    put the camera back. This must be the value the camera held BEFORE the
    //    write (`previousValue`, carried by buildApplyPlan) — the entry's own
    //    value is what the preset wants, and restoring that would re-apply the
    //    preset rather than undo it.
    rollbackByCamera[cameraKey] = {
      cameraKey,
      capturedAt: Date.now(),
      values: plan.writes
        .filter((w) => w.previousValue !== undefined)
        .map((w) => ({ name: w.targetName, value: w.previousValue as string | number | boolean }))
    }

    const total = plan.writes.length

    for (let i = 0; i < plan.writes.length; i++) {
      const write: ResolvedWrite = plan.writes[i]

      if (options.shouldCancel?.()) {
        status = 'cancelled'
        reason = 'cancelled by operator'
        break
      }
      if (Date.now() > deadline) {
        status = 'timeout'
        reason = `apply exceeded ${Math.round(APPLY_DEADLINE_MS / 1000)}s`
        break
      }
      // The camera dropped mid-apply — stop rather than writing into the void
      // (setConfigValue silently returns undefined once disconnected).
      if (!useCameraStore.getState()[cameraKey === 'primary' ? 'camera' : 'secondaryCamera']) {
        status = 'aborted'
        reason = 'camera disconnected during apply'
        break
      }

      options.onProgress?.(i + 1, total, write.entry.label || write.targetName)

      try {
        await withTimeout(
          setConfigWithBusyRetry(cameraKey, write.targetName, write.value),
          WRITE_TIMEOUT_MS,
          `set ${write.targetName}`
        )
        applied += 1
        if (write.adjusted) adjusted += 1
        consecutiveFailures = 0
      } catch (error) {
        if (error instanceof PresetTimeoutError) {
          queueSuspect = true
          status = 'timeout'
          reason = `${write.targetName} stopped responding`
          failures.push({ name: write.targetName, message: error.message })
          break
        }
        const message = error instanceof Error ? error.message : String(error)
        failures.push({ name: write.targetName, message })
        consecutiveFailures += 1
        console.warn(
          `[CAMERA-PRESET] ${cameraKey}: failed to set ${write.targetName}:`,
          message
        )
        if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
          status = 'aborted'
          reason = `${MAX_CONSECUTIVE_FAILURES} consecutive failures — camera may be in a bad state`
          break
        }
      }
    }

    if (status === 'completed' && failures.length > 0) status = 'partial'
  } catch (error) {
    status = 'aborted'
    reason = error instanceof Error ? error.message : String(error)
    console.error(`[CAMERA-PRESET] ${cameraKey}: apply threw:`, error)
  } finally {
    // Wake and resume ALWAYS. A parked camera with a suspended preview is a dead
    // booth; that must not be the cost of a failed apply.
    if (!queueSuspect) {
      await unifiedCameraUtils.wakeCamera(cameraKey).catch(() => false)
    }
    try {
      await options.resumePreview()
    } catch (error) {
      console.error(`[CAMERA-PRESET] ${cameraKey}: resumePreview failed:`, error)
    }
    applyInFlight[cameraKey] = false
  }

  // Health probe. A cheap config read proves the PTP channel still answers, so a
  // wedged camera is reported to the operator now instead of being discovered by
  // the next guest. Skipped when the queue is suspect — see above.
  let cameraHealthy = true
  if (queueSuspect) {
    cameraHealthy = false
  } else {
    try {
      const probe = await withTimeout(
        Promise.resolve(unifiedCameraUtils.getConfig(cameraKey)),
        CONFIG_READ_TIMEOUT_MS,
        'health probe'
      )
      cameraHealthy = Boolean(probe)
    } catch {
      cameraHealthy = false
    }
  }

  const result: ApplyResult = {
    status,
    applied,
    adjusted,
    skipped: plan.skipped.length,
    failures,
    durationMs: Date.now() - startedAt,
    cameraHealthy,
    reason
  }

  console.log(`[CAMERA-PRESET] ${cameraKey}: apply ${status}`, {
    applied,
    adjusted,
    skipped: result.skipped,
    failed: failures.length,
    durationMs: result.durationMs,
    cameraHealthy
  })

  return result
}

/**
 * PTP `0x2019` (device busy) is transient and extremely common right after a
 * mode change — the same escalating backoff `cameraSettingsManager` uses for the
 * live-view overrides. Non-busy errors are surfaced immediately: retrying a
 * rejected value just burns the apply deadline.
 */
async function setConfigWithBusyRetry(
  cameraKey: CameraKey,
  name: string,
  value: string | number | boolean,
  maxRetries = 3
): Promise<void> {
  let lastError: unknown
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      await unifiedCameraUtils.setConfigValue(cameraKey, name, value)
      return
    } catch (error) {
      lastError = error
      if (!isBusyError(error) || attempt === maxRetries) throw error
      await sleep(250 * (attempt + 1))
    }
  }
  throw lastError instanceof Error ? lastError : new Error(String(lastError))
}

/* ────────────────────────── app-side settings ───────────────────────── */

/**
 * The booth's own capture settings travel with the preset. They are the SAFEST
 * half of the feature — plain localStorage writes, no PTP, no 800ms settle,
 * nothing that can wedge a camera — and often the more valuable half:
 * `useShutterCapture` and the quality settings are what actually change the
 * photo the guest receives. They also give webcam booths and thin-config bodies
 * (Sony) something to carry when the gphoto2 tree yields almost nothing.
 */
export const APP_CAPTURE_KEYS = [
  'previewQuality',
  'captureQuality',
  'captureFormat',
  'useShutterCapture',
  'applyFilterOnPreview',
  'postShutterLiveviewDelayMs',
  'autoBrightenLiveViewForFlash',
  'liveViewShutterSpeedOverride'
] as const

/**
 * Geometry depends on how THIS booth is physically built — where the camera is
 * bolted, which way the monitor faces, what the frame expects. Carried so it is
 * never silently lost, but never applied unless the operator asks for it.
 */
export const APP_GEOMETRY_KEYS = [
  'mirror',
  'previewRotation',
  'captureRotation',
  'captureRatio'
] as const

export interface PresetAppSettings {
  capture: Record<string, unknown>
  geometry: Record<string, unknown>
}

function pick(source: Record<string, unknown>, keys: readonly string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const key of keys) {
    const value = source?.[key]
    if (value !== undefined) out[key] = value
  }
  return out
}

export function extractAppSettings(
  cameraSettings: Record<string, unknown> | undefined
): PresetAppSettings {
  const source = cameraSettings || {}
  return {
    capture: pick(source, APP_CAPTURE_KEYS),
    geometry: pick(source, APP_GEOMETRY_KEYS)
  }
}

/**
 * Restrict a stored blob to the keys we recognise. A row written by a newer
 * build must never be able to inject arbitrary keys into the settings store.
 */
export function selectAppSettingsToApply(
  stored: Partial<PresetAppSettings> | undefined,
  includeGeometry: boolean
): Record<string, unknown> {
  if (!stored) return {}
  return {
    ...pick(stored.capture || {}, APP_CAPTURE_KEYS),
    ...(includeGeometry ? pick(stored.geometry || {}, APP_GEOMETRY_KEYS) : {})
  }
}

/**
 * Put back the values from before the last apply.
 *
 * Deliberately NOT automatic on failure. A failed apply means the camera is
 * already unhappy, and the reflex of firing another N writes at it is how a
 * partial change becomes a wedged body. The operator decides, and only while
 * the camera is answering.
 */
export async function undoLastApply(
  cameraKey: CameraKey,
  options: ApplyOptions
): Promise<ApplyResult> {
  const rollback = rollbackByCamera[cameraKey]
  const startedAt = Date.now()
  if (!rollback) return refuse('nothing to undo', startedAt)

  const config = (await withTimeout(
    Promise.resolve(unifiedCameraUtils.getConfig(cameraKey)),
    CONFIG_READ_TIMEOUT_MS,
    'getConfig'
  ).catch(() => null)) as ConfigNode | null
  if (!config) return refuse('camera is not answering', startedAt)

  const index = indexByName(config)
  const writes: ResolvedWrite[] = rollback.values
    .filter((v) => {
      const node = index.get(v.name)
      return node && !node.readonly && String(node.value) !== String(v.value)
    })
    .map((v) => ({
      entry: {
        name: v.name,
        type: index.get(v.name)?.type || 'text',
        value: v.value,
        group: 'exposure' as PresetGroup
      },
      targetName: v.name,
      value: v.value,
      adjusted: false
    }))

  const result = await applyPresetPlan(
    cameraKey,
    { tier: 'exact', writes, skipped: [], noOps: [] },
    options
  )

  if (result.status === 'completed') {
    clearRollback(cameraKey)
  } else {
    // applyPresetPlan just replaced the rollback with the state from BEFORE the
    // undo — i.e. the preset's own values. Leaving that in place would turn a
    // retried "Undo" into "re-apply the preset". Put the original back so the
    // operator can simply try the undo again.
    rollbackByCamera[cameraKey] = rollback
  }
  return result
}
